import { Compass, Eye, Map as MapIcon, Building2, Globe, User } from 'lucide-react';
import ThemeToggle from '@/components/ThemeToggle';
import { Link, useLocation } from 'react-router-dom';
import { useI18n, LOCALE_LABELS } from '@/i18n';
import { useActiveLanguages } from '@/hooks/useSystemSettings';
import { useAuth } from '@/hooks/useAuth';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { motion } from 'framer-motion';
const logoDark = '/scuvio-logo-dark.svg';
const logoLight = '/scuvio-logo-light.svg';
import NotificationBell from '@/components/notifications/NotificationBell';
import { ControlPanelTrigger } from '@/components/layout/control-panel/AppControlPanel';

import type { LucideIcon } from 'lucide-react';

type NavLink = {
  to: string;
  key: string;
  icon: LucideIcon;
  /**
   * Additional path prefixes that should keep this top-nav entry active.
   * Used to highlight "Entdecken" while the user is browsing spots, the
   * map, visibility logs or POIs (all of which are part of the
   * Discovery area but live on legacy top-level routes).
   */
  activePrefixes?: string[];
};

/**
 * Public top navigation = Scuvio's öffentliche Drehscheibe.
 *
 * Strikt nur public/discoverable Inhalte: Guide, Sichtweiten, Karte,
 * Anbieter. Persönliche, Community-, Gruppen- und Admin-Funktionen
 * leben im internen `AppControlPanel` (Avatar-Trigger / persistentes
 * Sidepanel auf internen Routen).
 */
const mainNavLinks: NavLink[] = [
  { to: '/guide', key: 'nav.guide', icon: Compass, activePrefixes: ['/guide', '/results', '/site/', '/courses'] },
  { to: '/visibility', key: 'nav.visibility', icon: Eye, activePrefixes: ['/visibility', '/report'] },
  { to: '/map', key: 'nav.map', icon: MapIcon, activePrefixes: ['/map'] },
  { to: '/providers', key: 'nav.providers', icon: Building2, activePrefixes: ['/providers', '/poi/'] },
];

const Navbar = () => {
  const { pathname } = useLocation();
  const { t, locale, setLocale } = useI18n();
  const { user } = useAuth();
  const activeLanguages = useActiveLanguages();
  // Top nav is intentionally identical for anon and authenticated
  // users — only public discovery surfaces. Internal actions live in
  // the AppControlPanel triggered via the avatar.

  const isHome = pathname === '/';
  const isAdmin = pathname.startsWith('/admin');

  // Hide public navbar in admin area – AdminLayout has its own focused top bar.
  if (isAdmin) return null;

  return (
    <nav
      className={`hidden lg:block fixed top-0 left-0 right-0 z-[100] h-16 transition-colors duration-500 ${
        isHome
          ? 'bg-background/60 backdrop-blur-3xl border-b border-border/10'
          : 'bg-background/90 backdrop-blur-2xl border-b border-border/20'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2 group">
          <img src={logoLight} alt="Scuvio" className="h-8 w-auto hidden dark:block" />
          <img src={logoDark} alt="Scuvio" className="h-8 w-auto dark:hidden" />
        </Link>

        {/* Nav links */}
        <div className="flex items-center gap-1">
          {mainNavLinks.map(({ to, key, icon: Icon, activePrefixes }) => {
            const active =
              pathname === to ||
              (activePrefixes ?? []).some((p) => pathname.startsWith(p) && p !== '/');
            return (
              <Link
                key={to}
                to={to}
                className="relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 whitespace-nowrap group"
              >
                <Icon
                  className={`w-4 h-4 transition-colors duration-300 ${
                    active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                  }`}
                  strokeWidth={active ? 2 : 1.5}
                />
                <span
                  className={`transition-colors duration-300 ${
                    active ? 'text-foreground font-semibold' : 'text-muted-foreground group-hover:text-foreground'
                  }`}
                >
                  {t(key)}
                </span>
                {active && (
                  <motion.div
                    layoutId="nav-active-pill"
                    className="absolute inset-0 rounded-xl bg-primary/[0.08] dark:bg-primary/[0.12]"
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                  />
                )}
              </Link>
            );
          })}
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-1">
          <ThemeToggle />

          {user && <NotificationBell />}

          {activeLanguages.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-primary/[0.06] px-2.5 h-9 transition-colors duration-300"
                >
                  <Globe className="w-4 h-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">{locale}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8} className="rounded-xl min-w-[140px]">
                {activeLanguages.map((l) => (
                  <DropdownMenuItem
                    key={l}
                    onClick={() => setLocale(l)}
                    className={`gap-2.5 rounded-lg text-sm transition-colors ${
                      locale === l ? 'text-primary font-semibold bg-primary/[0.06]' : ''
                    }`}
                  >
                    {LOCALE_LABELS[l]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Divider */}
          <div className="w-px h-5 bg-border/30 mx-1.5" />

          {/* Auth / User Menu */}
          {user ? (
            <ControlPanelTrigger />
          ) : (
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                asChild
                className="gap-2 rounded-xl text-foreground hover:bg-primary/[0.08] h-9 transition-colors duration-200"
              >
                <Link to="/auth" state={{ from: pathname, mode: 'login' }}>
                  <User className="w-4 h-4" />
                  <span className="text-sm font-medium">{t('auth.login')}</span>
                </Link>
              </Button>
              <Button
                size="sm"
                asChild
                className="rounded-xl h-9 px-3.5 font-medium shadow-sm transition-colors duration-200"
              >
                <Link to="/auth" state={{ from: pathname, mode: 'signup' }}>
                  <span className="text-sm">{t('auth.signup')}</span>
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
